import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { ulid } from "ulid";

import {
  AddProductVariantSection,
  TabLabelWithCount,
  useSnackbar,
} from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { hasPricing } from "../purchase-return-rules";
import { ReturnableTypeEnum } from "../returnable-type";
import { ExpensesSection } from "./expenses-section";
import { GeneralInformationSection } from "./general-information-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { ProductVariantsSection } from "./product-variants-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./purchase-return-form-constants";
import { computePurchaseReturnTotals } from "./purchase-return-totals";
import { SelectGoodsReceiptProductVariantSection } from "./select-goods-receipt-product-variant-section";
import { SelectPurchaseInvoiceProductVariantSection } from "./select-purchase-invoice-product-variant-section";

export const PurchaseReturnForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
}) => {
  const methods = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState, control } = methods;
  const { showSnackbar } = useSnackbar();

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [tabIndex, setTabIndex] = useState(0);

  const [
    returnableType,
    returnableProductVariants,
    purchaseReturnExpenses,
    warehouse,
    buyer,
  ] = useWatch({
    control,
    name: [
      "returnableType",
      "returnableProductVariants",
      "purchaseReturnExpenses",
      "warehouse",
      "buyer",
    ],
  });

  // Changing the source empties the Biaya tab, so don't strand the user on it.
  useEffect(() => {
    setTabIndex(0);
  }, [returnableType]);

  const handleAddProductVariant = (productVariant, product, quantity) => {
    append({
      id: ulid(),
      returnableProductVariantId: null,
      warehouseId: warehouse?.id,
      name: productVariant.name,
      sku: productVariant.sku,
      quantity: Number(quantity),
      unitOfMeasurement: product.unitOfMeasurement.name,
      price: 0,
      discount: 0,
      note: null,
    });
  };

  const handleFormSubmit = async (formData) => {
    // The backend validates grandTotal against the goods figure alone and adds
    // the returned expenses itself, so send the goods total, not what the
    // summary displays.
    const { taxAmount, goodsGrandTotal } = computePurchaseReturnTotals(formData);

    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      buyerId: formData.buyer.id,
      sellerableId: formData.seller.id,
      sellerableType: formData.sellerableType,
      returnableId: formData.returnableId || null,
      returnableType:
        formData.returnableType === ReturnableTypeEnum.NON_INVOICE
          ? null
          : formData.returnableType,
      note: formData.note || null,
      taxable: formData.taxable,
      totalIncludesTax: formData.totalIncludesTax,
      taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountAmount,
      grandTotal: goodsGrandTotal,
      // Returned expenses reference the source invoice's own expense lines.
      // Every other source rejects them outright, so send none.
      ...(formData.returnableType === ReturnableTypeEnum.PURCHASE_INVOICE && {
        purchaseReturnExpenses: (formData.purchaseReturnExpenses ?? []).map(
          (expense) => ({
            purchaseInvoiceExpenseId: expense.purchaseInvoiceExpenseId,
            amount: expense.amount,
            note: expense.note || null,
          })
        ),
      }),
      returnableProductVariants: formData.returnableProductVariants.map(
        (variant) => ({
          returnableProductVariantId: variant.returnableProductVariantId,
          warehouseId: variant.warehouseId || warehouse?.id,
          sku: variant.sku,
          quantity: variant.quantity,
          discount: variant.discount || 0,
          price: variant.price || 0,
          note: variant.note || null,
        })
      ),
    };

    await onSubmit(payload);
  };

  // Validation errors can sit on a field no section renders (e.g. the
  // product-variants array while the Biaya tab is open), so make the
  // rejection visible and land the user on the offending tab.
  const onInvalid = (formErrors) => {
    if (formErrors.returnableProductVariants) {
      setTabIndex(0);
    }
    showSnackbar(
      formErrors.returnableProductVariants?.message ||
        "Periksa kembali isian yang belum valid",
      "error"
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
        onKeyDown={handleKeyDown}
        spacing={2.5}
      >
        <Grid size={12}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Tabs
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
              >
                <Tab
                  label={
                    <TabLabelWithCount
                      label="Barang"
                      count={returnableProductVariants.length}
                    />
                  }
                />
                <Tab
                  label={
                    <TabLabelWithCount
                      label="Biaya"
                      count={purchaseReturnExpenses.length}
                    />
                  }
                />
              </Tabs>
              <Divider />

              {tabIndex === 0 && (
                <Stack spacing={2.5}>
                  {returnableType === ReturnableTypeEnum.GOODS_RECEIPT && (
                    <SelectGoodsReceiptProductVariantSection />
                  )}
                  {returnableType === ReturnableTypeEnum.PURCHASE_INVOICE && (
                    <SelectPurchaseInvoiceProductVariantSection />
                  )}
                  {returnableType === ReturnableTypeEnum.NON_INVOICE && (
                    <AddProductVariantSection
                      outletId={buyer?.id}
                      onAdded={handleAddProductVariant}
                    />
                  )}

                  <Divider />
                  <Typography variant="h6">Retur Barang</Typography>
                  <ProductVariantsSection />
                </Stack>
              )}

              {tabIndex === 1 && <ExpensesSection />}
            </Stack>
          </Paper>
        </Grid>

        {hasPricing(returnableType) && (
          <>
            <Grid size={{ xs: 12, md: 5, lg: 7 }}>
              <OtherSection editMode={editMode} />
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 5 }}>
              <PriceSummarySection editMode={editMode} />
            </Grid>
          </>
        )}

        <Grid
          size={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

PurchaseReturnForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};
